import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Trash2, GripVertical, Save, Music, MapPin, 
  Clock, Users, DollarSign, Megaphone, RefreshCw, Eye
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { adminFetch } from '@/utils/adminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const defaultConfig = {
  serviceOptions: [
    'Samba Parade',
    'Stage Show',
    'Stilt Walkers',
    'Drummers Only',
    'Dancers Only',
    'Full Band',
    'Workshop/Class',
    'Team Building',
    'Photo Op/Meet & Greet',
    'Custom Performance'
  ],
  venueOptions: [
    'Indoor Venue',
    'Outdoor Venue',
    'Convention Center',
    'Hotel/Resort',
    'Private Residence',
    'Restaurant/Bar',
    'Festival/Fair',
    'Corporate Office',
    'School/University',
    'Other'
  ],
  budgetRanges: [
    'Under $1,000',
    '$1,000 - $2,500',
    '$2,500 - $5,000',
    '$5,000 - $10,000',
    '$10,000 - $25,000',
    '$25,000+',
    'Not Sure - Need Quote'
  ],
  durationOptions: [
    '15-30 minutes',
    '30-45 minutes',
    '45-60 minutes',
    '1-2 hours',
    '2+ hours',
    'Full day event'
  ],
  performerOptions: [
    '1-5 (Intimate)',
    '6-12 (Small Group)',
    '13-20 (Medium Group)',
    '21-35 (Large Group)',
    '35+ (Full Production)',
    'Not sure - need recommendation'
  ],
  howDidYouHearOptions: [
    'Google Search',
    'Social Media',
    'Referral',
    'Previous Event',
    'GigSalad/The Bash',
    'Other'
  ]
};

const EditableList = ({ title, icon: Icon, items, onUpdate, color = 'primary' }) => {
  const [newItem, setNewItem] = useState('');

  const addItem = () => {
    if (newItem.trim() && !items.includes(newItem.trim())) {
      onUpdate([...items, newItem.trim()]);
      setNewItem('');
    }
  };

  const removeItem = (index) => {
    onUpdate(items.filter((_, i) => i !== index));
  };

  const moveItem = (fromIndex, direction) => {
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= items.length) return;
    
    const newItems = [...items];
    [newItems[fromIndex], newItems[toIndex]] = [newItems[toIndex], newItems[fromIndex]];
    onUpdate(newItems);
  };

  const updateItem = (index, value) => {
    const newItems = [...items];
    newItems[index] = value;
    onUpdate(newItems);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 bg-${color}/20 rounded-lg`}>
          <Icon className={`w-5 h-5 text-${color}`} />
        </div>
        <h3 className="text-lg font-bold">{title}</h3>
        <span className="text-sm text-muted-foreground">({items.length} items)</span>
      </div>

      <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
        {items.map((item, index) => (
          <div key={index} className="flex items-center gap-2 group">
            <div className="flex flex-col">
              <button
                onClick={() => moveItem(index, -1)}
                disabled={index === 0}
                className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
              >
                <GripVertical className="w-3 h-3 rotate-90" />
              </button>
              <button
                onClick={() => moveItem(index, 1)}
                disabled={index === items.length - 1}
                className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
              >
                <GripVertical className="w-3 h-3 rotate-90" />
              </button>
            </div>
            <input
              type="text"
              value={item}
              onChange={(e) => updateItem(index, e.target.value)}
              className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              onClick={() => removeItem(index)}
              className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && addItem()}
          placeholder="Add new option..."
          className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <button
          onClick={addItem}
          disabled={!newItem.trim()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add
        </button>
      </div>
    </div>
  );
};

export const AdminQuoteFormEditor = () => {
  const [config, setConfig] = useState(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await adminFetch('/quote-form-config');
      if (response.ok) {
        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          setConfig({ ...defaultConfig, ...data });
        }
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      // Use defaults if fetch fails
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const response = await adminFetch('/admin/quote-form-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        toast.success('Quote form configuration saved!');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (window.confirm('Reset all options to defaults? This cannot be undone.')) {
      setConfig(defaultConfig);
      toast.success('Reset to default options');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="p-2 hover:bg-white/5 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold">Quote Form Editor</h1>
              <p className="text-muted-foreground">Customize the options shown in your quote request form</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/booking#quote-form"
              target="_blank"
              className="flex items-center gap-2 px-4 py-2 bg-stone-700 hover:bg-stone-600 rounded-lg transition-colors"
            >
              <Eye className="w-4 h-4" />
              Preview Form
            </Link>
            <button
              onClick={resetToDefaults}
              className="flex items-center gap-2 px-4 py-2 bg-stone-700 hover:bg-stone-600 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Reset Defaults
            </button>
            <button
              onClick={saveConfig}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg font-bold hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-primary/10 border border-primary/30 rounded-xl p-4 mb-8">
          <p className="text-sm text-foreground">
            <strong>How it works:</strong> Edit the options below to customize what appears in your quote request form. 
            You can add, remove, rename, or reorder any option. Changes are saved to the database and will appear on the booking page after saving.
          </p>
        </div>

        {/* Editor Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <EditableList
            title="Service Types"
            icon={Music}
            items={config.serviceOptions}
            onUpdate={(items) => setConfig({ ...config, serviceOptions: items })}
            color="primary"
          />

          <EditableList
            title="Venue Types"
            icon={MapPin}
            items={config.venueOptions}
            onUpdate={(items) => setConfig({ ...config, venueOptions: items })}
            color="blue-400"
          />

          <EditableList
            title="Performance Duration"
            icon={Clock}
            items={config.durationOptions}
            onUpdate={(items) => setConfig({ ...config, durationOptions: items })}
            color="green-400"
          />

          <EditableList
            title="Number of Performers"
            icon={Users}
            items={config.performerOptions}
            onUpdate={(items) => setConfig({ ...config, performerOptions: items })}
            color="purple-400"
          />

          <EditableList
            title="Budget Ranges"
            icon={DollarSign}
            items={config.budgetRanges}
            onUpdate={(items) => setConfig({ ...config, budgetRanges: items })}
            color="yellow-400"
          />

          <EditableList
            title="How Did You Hear About Us"
            icon={Megaphone}
            items={config.howDidYouHearOptions}
            onUpdate={(items) => setConfig({ ...config, howDidYouHearOptions: items })}
            color="pink-400"
          />
        </div>

        {/* Save Button (Bottom) */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={saveConfig}
            disabled={saving}
            className="flex items-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-xl font-bold text-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {saving ? (
              <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            Save All Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminQuoteFormEditor;
